<template>
  <base-layout :do-skip-header="false" :doSkipPic="shouldSkipPic">
    <template v-if="user" #main>
      <!-- Mobile Contacts -->
      <div v-show="showContacts" class="w-full border-r overflow-y-auto md:hidden">
        <h2 class="text-lg font-bold mb-4">Chats</h2>
        <ul>
          <li v-for="contact in contacts"
            :key="contact.contactId"
            @click="fetchMessages(contact)"
            :class="[
              'cursor-pointer p-2 rounded hover:bg-blue-50 flex items-center gap-3',
              selectedContact?.contactId === contact.contactId ? 'bg-blue-100' : '',
              contact.unreadMessages && contact.unreadMessages > 0 ? 'font-bold' : '']">

            <div class="w-10 h-10 rounded-full bg-cover bg-center border" 
              :class="contactBorderColor(contact.contactId)"
              :style="{ backgroundImage: `url(${contact.profilePic})` }"></div>
            <span v-if="contact.unreadMessages && contact.unreadMessages > 0" 
              class="text-xs p-1 -ml-6 -mt-4 text-white rounded-full bg-red-500">{{ contact.unreadMessages }}</span>
            
            <div class="flex flex-col">
              <div class="flex items-center gap-2">
                <span>{{ contact.name }}</span>
                <!-- <span v-if="contact.unreadMessages" class="w-3 h-3 rounded-full bg-blue-500"></span> -->
              </div>

              <!-- Typing indicator -->
              <div v-if="typingUsers.includes(String(contact.contactId))" class="flex items-center gap-1 mt-1">
                <span v-for="n in 4" :key="n" class="dot"></span>
              </div>
            </div>
          </li>
        </ul>
      </div>
      
      <!-- Mobile Chat View -->
      <div v-show="!showContacts" class="flex flex-col h-screen md:h-[450px] md:hidden" v-cloak>
        <!-- Scrollable messages container (header is sticky inside this) -->
        <div ref="messageContainerMobile" class="flex-1 overflow-y-auto relative">
          <!-- Contact Information (sticky) -->
          <div v-if="selectedContact" class="sticky top-0 z-10 bg-gray-50 border-b px-2 py-3 flex items-center gap-3">
            <div @click="hideConversation()" class="py-3">
              <svg fill="#000000" width="20px" height="20px" viewBox="0 0 1024 1024">
                <path d="M222.927 580.115l301.354 328.512c24.354 28.708 20.825 71.724-7.883 96.078s-71.724 20.825-96.078-7.883L19.576 559.963a67.846 67.846 0 01-13.784-20.022 68.03 68.03 0 01-5.977-29.488l.001-.063a68.343 68.343 0 017.265-29.134 68.28 68.28 0 011.384-2.6 67.59 67.59 0 0110.102-13.687L429.966 21.113c25.592-27.611 68.721-29.247 96.331-3.656s29.247 68.721 3.656 96.331L224.088 443.784h730.46c37.647 0 68.166 30.519 68.166 68.166s-30.519 68.166-68.166 68.166H222.927z"/>
              </svg>
            </div>
            <div class="w-10 h-10 rounded-full bg-cover bg-center border"
                :class="contactBorderColor(selectedContact.contactId)"
                :style="{ backgroundImage: `url(${selectedContact?.profilePic || ''})` }">
            </div>
            <div class="cursor-pointer">
              <div class="font-bold text-sm"
                  @click="goToCompanyDetails(selectedContact.companyId)">
                {{ selectedContact?.name || '...' }}
              </div>
              <div class="text-xs text-gray-500"
                  @click="goToCompanyDetails(selectedContact.companyId)">
                {{ selectedContact?.companyName || '' }}
              </div>
              <div v-if="typingUsers.includes(String(selectedContact.contactId))" class="flex items-center gap-1 mt-1">
                <span v-for="n in 4" :key="n" class="dot"></span>
              </div>
            </div>
          </div>

          <!-- Message List -->
          <div class="p-3 space-y-3">
            <template v-if="isLoadingMessages">
              <div class="flex justify-start">
                <div class="bg-gray-200 p-3 rounded-xl shadow w-2/3 max-w-[80%] animate-pulse">
                  <div class="h-10 w-full rounded bg-opacity-30"></div>
                </div>
              </div>

              <div class="flex justify-end">
                <div class="bg-gray-200 p-3 rounded-xl shadow w-2/3 max-w-[80%] animate-pulse">
                  <div class="h-10 w-full rounded bg-opacity-30"></div>
                </div>
              </div>
            </template>

            <template v-else-if="activeConversation && activeConversation.length">
              <div v-for="(msg, index) in activeConversation"
                :key="index"
                :class="[ isFromMe(msg.contactId) ? 'items-end' : 'items-start', 'flex', 'flex-col', 'mb-2' ]">

                <!-- Last read divider -->
                <div v-if="index === firstUnreadFromMeIndex && showLastReadIndicator"
                  class="flex items-center text-gray-400 text-xs mb-2 w-full">
                  <div class="flex-grow border-t border-gray-300"></div>
                  <span class="mx-2">Last read by {{ selectedContact?.name }}</span>
                  <div class="flex-grow border-t border-gray-300"></div>
                </div>

                <!-- Message bubble -->
                <div :class="[ isFromMe(msg.contactId) ? 'bg-blue-500 text-white' : 'bg-white text-black',
                  'p-3 rounded-xl shadow max-w-[80%] break-all overflow-wrap-break-word' ]">
                  {{ msg.content }}
                </div>

                <!-- Timestamp -->
                <div class="text-xs text-gray-300 mt-1 mx-2">
                  {{ new Date(msg.dateCreated).toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  }) }}
                </div>

              </div>
            </template>

            <div v-else-if="activeConversation && activeConversation.length === 0" class="h-[350px]"></div>

            <div v-else class="text-gray-400 text-center mt-10">
              Select a conversation
            </div>
          </div>
        </div>

        <!-- Input Area (stays at bottom via flex layout; sticky included for extra safety) -->
        <div class="border-t bg-white p-2 flex items-center sticky bottom-0 z-20">
          <input 
            ref="messageInputMobile"
            v-model="newMessage"
            @input="sendTyping"
            @keydown.enter="sendMessage"
            type="text"
            placeholder="Type a message..."
            class="flex-1 border border-gray-300 rounded-xl p-2 mr-2 focus:outline-none"
          />
          <button @click="sendMessage" class="bg-blue-500 text-white px-4 py-2 rounded-xl hover:bg-blue-600">
            <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" >
              <path d="M11.5003 12H5.41872M5.24634 12.7972L4.24158 15.7986C3.69128 17.4424 3.41613 18.2643 3.61359 18.7704C3.78506 19.21 4.15335 19.5432 4.6078 19.6701C5.13111 19.8161 5.92151 19.4604 7.50231 18.7491L17.6367 14.1886C19.1797 13.4942 19.9512 13.1471 20.1896 12.6648C20.3968 12.2458 20.3968 11.7541 20.1896 11.3351C19.9512 10.8529 19.1797 10.5057 17.6367 9.81135L7.48483 5.24303C5.90879 4.53382 5.12078 4.17921 4.59799 4.32468C4.14397 4.45101 3.77572 4.78336 3.60365 5.22209C3.40551 5.72728 3.67772 6.54741 4.22215 8.18767L5.24829 11.2793C5.34179 11.561 5.38855 11.7019 5.407 11.8459C5.42338 11.9738 5.42321 12.1032 5.40651 12.231C5.38768 12.375 5.34057 12.5157 5.24634 12.7972Z" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>



      <div class="h-[80vh] flex mx-4 shadow-md rounded-xl bg-white p-4 hidden md:flex" v-cloak>
        <!-- Sidebar -->
        <!-- Desktop Contacts -->
        <div ref="contactContainer" class="w-80 bg-white border-r p-4 overflow-y-auto">
          <h2 class="text-lg font-bold mb-4">Chats</h2>
          <ul>
            <li v-for="contact in contacts"
              :key="contact.contactId" @click="fetchMessages(contact)"
              :class="['cursor-pointer p-2 rounded hover:bg-blue-50 flex items-center gap-3',
              selectedContact?.contactId === contact.contactId ? 'bg-blue-100' : '',
              contact.unreadMessages && contact.unreadMessages > 0 ?  'font-bold' : '']">

              <div class="w-10 h-10 rounded-full bg-cover bg-center border" 
                :class="contactBorderColor(contact.contactId)"
                :style="{ backgroundImage: `url(${contact.profilePic})` }"></div>

               <div class="flex flex-col">
                <div class="flex items-center gap-2">
                  <span v-if="contact.unreadMessages && contact.unreadMessages > 0" 
                    class="text-white text-[10px] p-1 rounded-full bg-red-500">{{ contact.unreadMessages }}
                  </span>
                  <span>{{ contact.name }}</span>
                </div>

                <!-- Typing indicator -->
                <div v-if="typingUsers.includes(String(contact.contactId))" class="flex items-center gap-1 mt-1">
                  <span v-for="n in 4" :key="n" class="dot"></span>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Chat Panel -->
        <div class="flex-1 flex flex-col bg-gray-50">
          <div ref="messageContainer" class="flex-1 overflow-y-auto relative">
            <!-- Contact Information -->
            <div  v-if="selectedContact" class="sticky top-0 z-10 bg-gray-50 border-b px-4 py-3 flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-cover bg-center border border-gray-400"
                :style="{ backgroundImage: `url(${selectedContact?.profilePic || ''})` }">
              </div>
              <div class="cursor-pointer">
                <div class="font-bold text-sm" @click="goToCompanyDetails(selectedContact.companyId)">{{ selectedContact?.name || '...' }}</div>
                <div class="text-xs text-gray-500" @click="goToCompanyDetails(selectedContact.companyId)">{{ selectedContact?.companyName || '' }}</div>
              </div>
            </div>

            <!-- Message List -->
            <div class="flex-1 overflow-y-auto p-4 space-y-3">
              <template v-if="isLoadingMessages">
                <div class="flex justify-start">
                  <div class="bg-gray-200 p-3 rounded-xl shadow w-2/3 max-w-[80%] animate-pulse">
                    <div class="h-10 w-full rounded bg-opacity-30"></div>
                  </div>
                </div>

                <div class="flex justify-end">
                  <div class="bg-gray-200 p-3 rounded-xl shadow w-2/3 max-w-[80%] animate-pulse">
                    <div class="h-10 w-full rounded bg-opacity-30"></div>
                  </div>
                </div>
            
                <div class="flex justify-start">
                  <div class="bg-gray-200 p-3 rounded-xl shadow w-2/3 max-w-[80%] animate-pulse">
                    <div class="h-20 w-full rounded bg-opacity-30"></div>
                  </div>
                </div>

                <div class="flex justify-end">
                  <div class="bg-gray-200 p-3 rounded-xl shadow w-2/3 max-w-[80%] animate-pulse">
                    <div class="h-10 w-full rounded bg-opacity-30"></div>
                  </div>
                </div>
              </template>
              <template v-else-if="activeConversation && activeConversation.length">
                <div v-for="(msg, index) in activeConversation"
                  :key="index"
                  :class="isFromMe(msg.contactId) ? 'items-end' : 'items-start'"
                  class="flex flex-col mb-2">

                  <!-- Last read divider -->
                  <div v-if="index === firstUnreadFromMeIndex && showLastReadIndicator"
                    class="flex items-center text-gray-400 text-xs mb-3 w-full">
                    <div class="flex-grow border-t border-gray-300"></div>
                    <span class="mx-2">Last read by {{ selectedContact?.name }}</span>
                    <div class="flex-grow border-t border-gray-300"></div>
                  </div>

                  <!-- Message bubble -->
                  <div :class="isFromMe(msg.contactId) ? 'bg-blue-500 text-white' : 'bg-white text-black'"
                    class="p-3 rounded-xl shadow max-w-[80%] break-all overflow-wrap-break-word">
                    {{ msg.content }}
                  </div>

                  <!-- Timestamp -->
                  <div class="text-xs text-gray-300 mt-1 mx-2">
                    {{ new Date(msg.dateCreated).toLocaleString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    }) }}
                  </div>

                </div>
              </template>

              <div v-else-if="selectedContact && activeConversation.length === 0" class="text-gray-400 text-center mt-20">
                Chatting with {{ selectedContact.name }}
              </div>
              <div v-else class="text-gray-400 text-center mt-10">Select a conversation</div>
            </div>
          </div>
           
          <!-- Input Area -->
          <div v-if="activeContact" class="border-t bg-white pt-4 px-2 flex items-center">
            <input 
              ref="messageInput" v-model="newMessage" @keydown.enter="sendMessage" type="text" @input="sendTyping"
              placeholder="Type a message..." class="flex-1 border border-gray-300 rounded-xl p-2 mr-2 focus:outline-none"/>
            
              <button @click="sendMessage" class="bg-blue-500 text-white px-4 py-2 rounded-xl hover:bg-blue-600">
                Send
              </button>
          </div>
        </div>
      </div>    
    </template>
  </base-layout>
</template>

<script>
import _ from "lodash";
import { mapState, mapActions, mapMutations } from "vuex";
import eventBus from "../utils/eventBus";


import BaseLayout from "../components/BaseLayout.vue";

export default {
  name: "Register",
  components: {
    BaseLayout,
  },
  data() {
    return {
      shouldSkipPic: true,
      messages: null,
      activeContact: null,
      newMessage: "",
      contacts: [],
      selectedConversationId: null,
      selectedContact: null,
      conversations: [],
      paramContactId: null,
      isLoadingMessages: false,
      showContacts: true,
      isMobile: window.innerWidth < 768,
      showLastReadIndicator: true,
      activeUsers: [],
      typing: false, 
      typingTimeout: null,
      typingUsers: [] 
    }
  },
  watch: {
    unreadMessages: {
      handler(newVal) {
        if (newVal) {
		      this.updateUnreadMessageCounts()
        }
      },
      deep: true,
      immediate: false
    },
  },
  beforeRouteEnter(to, from, next) {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      next();
    } else {
      next({ name: "home" });
    }
  },
  beforeRouteLeave(to, from, next) {
    this.setNewMessageIndicator(false)
    if (this.typing) {
      this.sendStopTyping();
    }
    next()
  },
  async created() {
    // Load the contacts from the local storage
    const savedContacts = sessionStorage.getItem("contacts");
    const savedActiveUsers = sessionStorage.getItem("activeUsers");

    if (savedContacts) {
      this.contacts = JSON.parse(savedContacts);
    }
    if(savedActiveUsers) {
      this.activeUsers = JSON.parse(savedActiveUsers)
    }

    try {
      const contactId = this.$route.query.contact
      this.paramContactId = contactId
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2-second delay | TEMPORARY

      if (contactId) {
        await this.createConversation(contactId)
        this.setActiveCoversation()        
      }
      await this.fetchContacts()
      this.fetchProfles()
      this.getActiveUsers()
    } catch (err) {
      console.log("err", err)
    }
  },
  mounted() {
    // this.initSharedWorker();
    // window.onbeforeunload = () => {
    //   this.workerPort.postMessage('closing');
    // };
    this.setNewMessageIndicator(false);
    eventBus.$on("new-message", this.handleIncomingMessage);
    eventBus.$on("message-read", this.handleMessageRead);
    eventBus.$on("user-connected", this.handleNewUserConnected);
    eventBus.$on("user-disconnected", this.handleUserDisconnected);
    eventBus.$on("user-typing", this.handleUserTyping);
    eventBus.$on("user-stopped-typing", this.handleUserStopTyping);


    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 768;
    });
  },
  beforeDestroy() {
    eventBus.$off("new-message", this.handleIncomingMessage);
    eventBus.$off("message-read", this.handleMessageRead);
    eventBus.$off("user-connected", this.handleNewUserConnected);
    eventBus.$off("user-disconnected", this.handleUserDisconnected);
    eventBus.$off("user-typing", this.handleUserTyping);
    eventBus.$off("user-stopped-typing", this.handleUserStopTyping);
  },
  computed: {
    ...mapState(["user", "socket", 'unreadMessages']),
    activeConversation() {
      return this.conversations
    },
    firstUnreadFromMeIndex() {
      return this.activeConversation.findIndex(
        msg => this.isFromMe(msg.contactId) && msg.dateRead === null
      );
    },
    contactBorderColor() {
      return (contactId) => {
        return this.activeUsers.includes(String(contactId))
          ? 'border-green-400 border-4'
          : 'border-gray-400';
      };
    },
  },

  methods: {
    ...mapMutations(['setNewMessageIndicator']),
    ...mapActions(['getUnreadMessageCount']),
    async sendMessage() {
      if(this.newMessage.length < 1) {
        return
      }
      const payload = {
        message: this.newMessage,
        conversationId: this.selectedConversationId
      }

      const res = await this.$http.post("/api/messages", payload )
      if(res.status === 201) {
        this.conversations.push(res.data.newMessage)
        this.scrollToBottom()
        this.newMessage = ""

        this.reloadMessage(true)

        // Remove the selected conversation from the original position
        const selectedConversation = this.contacts.find(c => c.conversationId == this.selectedConversationId)
        this.contacts = this.contacts.filter(
          c => c.conversationId != this.selectedConversationId
        );
        // Insert it at the beginning
        this.contacts.unshift(selectedConversation)
        this.scrollToTop()

        const contactId = this.$route.query.contact
        if(this.selectedContact.contactId != contactId) {
          // Change the paramerter value
          this.$router.replace({
            query: {
              ...this.$route.query,
              contact: selectedConversation.contactId
            }
          })
        }
      }
    },
    async createConversation(contactId) {
      const payload = {
        contactId: contactId
      }
      
      try {
        const res = await this.$http.post("/api/messages/conversation", payload)
      } catch (err) {
        console.error('Failed to create conversation:', err);
      }
    }, 
    async fetchContacts() {
      const contactId = this.$route.query.contact
      const res = await this.$http.get("/api/messages/conversation", {
        params: {selectedContact: contactId }
      })
      this.contacts = res.data.contacts
      .filter(contact => contact.email !== '-' && contact.name !== 'To be decided')
      .map(contact => {
        return {
          ...contact,
          profilePic: `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&background=fff&color=aaa`,
        }
      })
      this.setActiveCoversation()        
      sessionStorage.setItem("contacts", JSON.stringify(this.contacts))      
    },
    async fetchProfles() {
      const res = await this.$http.get("/api/messages/conversation/profilePictures")
      res.data.profilePictures.forEach(pic => {
        const contact = this.contacts.find(c => c.contactId === pic.id)
        if (contact && pic.profilePic) {
          contact.profilePic = pic.profilePic;
        } 
      });
    },
    async reloadMessage(addDelay = false) {
      // No selectec conversation
      if(!this.selectedConversationId){
        return;
      }

      if(addDelay) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      const res = await this.$http.get("/api/messages", {
        params: { conversationId: this.selectedConversationId }
      })
      if (Array.isArray(res.data.messages) && res.data.messages.length) {
        this.conversations = [...res.data.messages].reverse()
      }
      this.showLastReadIndicator = true
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.scrollToBottom();
        });
      });
      
    }, 
    async fetchMessages(contact) {
      if(contact.contactId == this.selectedContact?.contactId && !this.isMobile) {
        return
      }
      this.sendStopTyping()
      this.isLoadingMessages = true
      this.showContacts = false
      this.selectedConversationId = contact.conversationId
      this.selectedContact = contact
      this.activeContact = contact.name
      this.conversations = []
      this.showLastReadIndicator = true
      this.resetUnreadMessageCount()
      // this.focusMessageInput()

      const res = await this.$http.get("/api/messages", {
        params: { conversationId: contact.conversationId }
      })
      if (Array.isArray(res.data.messages) && res.data.messages.length) {
        this.conversations = [...res.data.messages].reverse()
      }
      this.isLoadingMessages = false
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.scrollToBottom();
        });
      });

      // this.$nextTick(() => {
      //   setTimeout(() => {
      //     const container = this.$refs.messageContainer;
      //     if (container && container.parentNode) {
      //       this.scrollToBottom();
      //     }
      //   }, 50); // small delay to avoid ResizeObserver race
      // });


    },
    async resetUnreadMessageCount() {
      if(!this.selectedConversationId) {
        return;
      }
      const payload = {
        conversationId : this.selectedConversationId
      }

      const res = await this.$http.post("/api/messages/resetUnreadMessage", payload)
      this.getUnreadMessageCount();
      if(res.data.status === 200) {
        const contact = this.contacts.find(c => c.contactId == this.selectedContact.contactId)
        if (contact) {
          // Remove the red circle (new message indicator)
          contact.unreadMessages = 0;
        }
      }
    },
    async getActiveUsers() {
      const res = await this.$http.post("/api/messages/getActiveUsers")
      if(res.status === 200) {
        this.activeUsers = res.data.users
        sessionStorage.setItem("activeUsers", JSON.stringify(this.activeUsers))      
      }
    },

    isFromMe(id) {
      return this.user.id === id
    },
    // scrollToBottom() {
    //   this.$nextTick(() => {
    //     const el = this.isMobile
    //     ? this.$refs.messageContainerMobile
    //     : this.$refs.messageContainer
    //     if (el) el.scrollTop = el.scrollHeight
    //   });
    // },
    scrollToBottom() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const el = this.isMobile
            ? this.$refs.messageContainerMobile
            : this.$refs.messageContainer;
          if (el) el.scrollTop = el.scrollHeight;
        });
      });
    },
    scrollToTop() {
      this.$nextTick(() => {
        const el = this.$refs.contactContainer
        if (el) el.scrollTop = 0
      });
    },
    focusMessageInput() {
      this.$nextTick(() => {
        const targetRef = this.isMobile 
          ? this.$refs.messageInputMobile 
          : this.$refs.messageInput;
        targetRef?.focus();
      });
    },
    setContactAsFirst(contactId) {
      const contact = this.contacts.find(c => c.contactId == contactId)
      if(contact) {
        // Remove the selected contact from its original position
        this.contacts = this.contacts.filter(
          c => c.contactId !== contactId
        );

        // Insert it at the beginning
        this.contacts.unshift(contact)
      }
    },
    setActiveCoversation() {
      if(this.paramContactId) {
        const contact = this.contacts.find(c => c.contactId == this.paramContactId)
        if(contact){
          this.fetchMessages(contact)
          if(contact.messageCount == 0){
            this.setContactAsFirst(contact.contactId)
          }
        }
      }
    },
    hideConversation() {
      // Step 1: Let keyboard collapse first
      if (document.activeElement) {
        document.activeElement.blur();
      }

      // Step 2: Delay the DOM teardown slightly
      setTimeout(() => {
        this.showContacts = true;
        this.selectedConversationId = null;
        this.selectedContact = null;
      }, 150); // 100–150ms works well
    },
    goToCompanyDetails(id) {
      const url = this.$router.resolve({ path: `/attendeeDetails/${id}` }).href;
			window.open(url, '_blank');
    },
    newMessageReceived(contactId, newMessage) {
      // If the conversation is already selected, just push the message 
      if(contactId == this.selectedContact?.contactId) {
        this.showLastReadIndicator = false
        // this.conversations.push(newMessage)
        this.reloadMessage()
        // this.scrollToBottom()
        this.resetUnreadMessageCount()
        this.setContactAsFirst(contactId)
        return
      }
      else {
        // Make the contact name bold
        const contact = this.contacts.find(c => c.contactId == contactId)
        if (!contact) {
          // If the contact is not yet in the list, refetch the contacts
          this.fetchContacts()
          this.fetchProfles()
        }
        // Move the contact to the first index
        this.setContactAsFirst(contactId)
        
      }
    },
    handleIncomingMessage(payload) {
      this.newMessageReceived(payload.from, payload.newMessage);
    },
    removeNewMessageIndicator(contactId) {
      const contact = this.contacts.find(c => c.contactId == contactId)
      if (contact) {
        // Remove the red circle (new message indicator)
        contact.unreadMessages = 0;
      }
    }, 
    handleMessageRead({ contactId }) {
      if(contactId == this.selectedContact?.contactId) {
        this.showLastReadIndicator = false
      }
    },
    handleNewUserConnected({ contactId }) {
      const id = String(contactId);
      if (!this.activeUsers.includes(id)) {
        this.activeUsers.push(id);
      }
    },
    handleUserDisconnected({ contactId }) {
      this.activeUsers = this.activeUsers.filter(id => id !== String(contactId))
    },
    sendTyping() {
      if (!this.socket || this.newMessage.trim() === "" || !this.selectedContact) {
        return;
      }

      // Send "user-typing" only if we're not already marked as typing
      if (!this.typing) {
        this.typing = true;
        this.socket.send(
          JSON.stringify({
            type: "user-typing",
            contactId: this.selectedContact.contactId
          })
        );
      }

      // Reset the stop-typing timer
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        this.sendStopTyping();
      }, 5000);
    },
    sendStopTyping() {
      if (!this.typing) return;
      this.typing = false;
      this.socket.send(
        JSON.stringify({
          type: "user-stopped-typing",
          contactId: this.selectedContact.contactId
        })
      );
    },
    handleUserTyping({ contactId }) {
      if (contactId && !this.typingUsers.includes(contactId)) {
        this.typingUsers.push(contactId);
      }
    },
    handleUserStopTyping({ contactId }) {
      this.typingUsers = this.typingUsers.filter(id => id !== contactId);
    },
    updateUnreadMessageCounts() {
      if (!Array.isArray(this.unreadMessages) || !Array.isArray(this.contacts)) {
        return;
      }
      
      // Loop through each unreadMessages entry
      _.forEach(this.unreadMessages, ({ contactId, unreadMessages }) => {
        // Find the contact with the same ID
        const contact = _.find(this.contacts, { contactId });

        if (contact) {
          // Update the contact's unreadMessages value
          contact.unreadMessages = unreadMessages;
        }
      });

    }
  },

};
</script>

<style>
.dot {
  width: 6px;
  height: 6px;
  background-color: #555;
  border-radius: 50%;
  display: inline-block;
  animation: bounce 1s infinite ease-in-out;
}

.dot:nth-child(1) { animation-delay: 0s; }
.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }
.dot:nth-child(4) { animation-delay: 0.6s; }

.break-all {
  word-break: break-all;
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}
</style>